import { images } from './images';
import type { Benefit, Faq, Step } from '../types/content';

export const aboutValues: Benefit[] = [
  { title: 'Practical', text: 'Every lesson teaches English you can use outside class on the same day.' },
  { title: 'Personal', text: 'Your teacher knows your goals and gives you feedback that is about you.' },
  { title: 'International', text: 'Classmates from many countries help you hear and use English in many ways.' },
  { title: 'Safe', text: 'Clear rules, trained staff and student support at every campus and in every home we arrange.' },
];

export const studentSupport: Benefit[] = [
  { title: 'Student support in many languages', text: 'Ask questions in your own language before and after you arrive.' },
  { title: 'A dedicated careers office', text: 'Get advice on jobs, study plans and your next steps.' },
  { title: 'Free workshops and career seminars', text: 'Learn how to write applications and prepare for interviews.' },
  { title: 'Job listings on the student portal', text: 'See open roles that suit English-speaking students.' },
  { title: 'A free CV generator', text: 'Create a clear CV in English with an easy online tool.' },
  { title: 'Interview practice', text: 'Practise real job interviews in English with our team.' },
];

export const lessonStages: Step[] = [
  { title: 'Warm-up', text: 'You talk with a partner about the topic. This helps you remember words you already know.' },
  { title: 'New language', text: 'Your teacher shows new words or grammar in a real text or recording.' },
  { title: 'Controlled practice', text: 'You practise the new language in short, clear tasks with help.' },
  { title: 'Real use', text: 'You use the language in a speaking or writing task, such as a role play.' },
  { title: 'Feedback', text: 'Your teacher shows what went well and what to improve next time.' },
];

export const levels: { name: string; cefr: string; text: string }[] = [
  { name: 'Beginner', cefr: 'A1', text: 'Use simple words and phrases about yourself.' },
  { name: 'Elementary', cefr: 'A2', text: 'Talk about daily life, shopping and travel.' },
  { name: 'Pre-intermediate', cefr: 'B1', text: 'Describe experiences and give simple opinions.' },
  { name: 'Intermediate', cefr: 'B1+', text: 'Join longer conversations on familiar topics.' },
  { name: 'Upper-intermediate', cefr: 'B2', text: 'Speak with confidence on most topics.' },
  { name: 'Advanced', cefr: 'C1', text: 'Use English well for work and study.' },
  { name: 'Proficiency', cefr: 'C2', text: 'Understand and use English with ease in almost any situation.' },
];

export const progressChecks: Benefit[] = [
  { title: 'Weekly feedback', text: 'Your teacher gives you personal feedback every week.' },
  { title: 'Progress tests', text: 'You take a short test at regular points to check your progress.' },
  { title: 'Level reports', text: 'At the end of each level, you receive a report in the student portal.' },
  { title: 'Certificate', text: 'You receive a certificate with your level when you finish your course.' },
];

export const openRoles: { title: string; type: string; location: string }[] = [
  { title: 'General English teacher', type: 'Full-time', location: 'Sample campus' },
  { title: 'IELTS teacher', type: 'Part-time', location: 'Sample campus' },
  { title: 'Student services adviser', type: 'Full-time', location: 'Sample campus' },
  { title: 'Activity leader (summer camps)', type: 'Seasonal', location: 'Sample campus' },
];

export const careerBenefits: Benefit[] = [
  { title: 'Training every term', text: 'Join workshops and observations that help you grow as a teacher.' },
  { title: 'A clear career path', text: 'Move from teacher to senior teacher, trainer or academic manager.' },
  { title: 'An international team', text: 'Work with colleagues from many countries and share ideas.' },
];

export const applySteps: Step[] = [
  { title: 'Send your CV', text: 'Email your CV and a short note about the role you want.' },
  { title: 'Meet our team', text: 'Join an online interview. Teachers also give a short demo lesson.' },
  { title: 'Start with support', text: 'Receive an induction and a mentor in your first weeks.' },
];

export const galleryItems: { src: string; alt: string; category: string }[] = [
  { src: images.hero, alt: 'Students talking around a table in a school lounge', category: 'Student life' },
  { src: images.classroom, alt: 'A teacher at an interactive screen with students', category: 'Classrooms' },
  { src: images.teacher, alt: 'A teacher helping two students with a workbook', category: 'Classrooms' },
  { src: images.accommodation, alt: 'A modern student studio with a bed and a desk', category: 'Accommodation' },
  { src: images.social, alt: 'Students on a city walking tour', category: 'Student life' },
  { src: images.young, alt: 'Teenagers doing a team activity at a summer camp', category: 'Student life' },
  { src: images.dubai, alt: 'City view near one of our campuses', category: 'Locations' },
  { src: images.london, alt: 'Street scene near one of our campuses', category: 'Locations' },
  { src: images.tokyo, alt: 'Quiet city street near one of our campuses', category: 'Locations' },
  { src: images.oxfordshire, alt: 'Historic campus buildings at a seasonal location', category: 'Locations' },
];

export const roomTypes: { name: string; group: string; text: string; features: string[] }[] = [
  { name: 'Single room', group: 'Shared spaces', text: 'A private en-suite room with your own bed and bathroom, plus a shared kitchen.', features: ['Private bathroom', 'Shared kitchen and fridge', 'Desk and storage'] },
  { name: 'Shared room', group: 'Shared spaces', text: 'A spacious twin room with two single beds, a shared bathroom and a workspace.', features: ['Two single beds', 'Shared bathroom', 'Workspace'] },
  { name: 'Studio', group: 'Private spaces', text: 'A self-contained apartment with a private kitchenette, study area and living space.', features: ['Private kitchenette', 'Study area', 'Living space'] },
  { name: 'One bedroom', group: 'Private spaces', text: 'A larger apartment with a king-size bed, sofa, private kitchen and en-suite bathroom.', features: ['King-size bed', 'Private kitchen', 'En-suite bathroom'] },
];

export const accommodationFacilities: string[] = ['Free gym access', 'Laundry service', 'Free Wi-Fi', 'Housekeeping', 'Swimming pool', 'Kitchen or kitchenette'];

export const accommodationPrices: { room: string; price: string }[] = [
  { room: 'Shared room', price: '000' },
  { room: 'Single room', price: '000' },
  { room: 'Studio', price: '000' },
  { room: 'One bedroom', price: '000' },
];

export const sameEverywhere: Benefit[] = [
  { title: 'One teaching method', text: 'Lessons follow the same plan and the same level system at every campus.' },
  { title: 'One support team', text: 'Student services help you in the same way, wherever you study.' },
  { title: 'One certificate', text: 'Your ES World certificate shows your level in the same format at every location.' },
];

export const choosingTips: Benefit[] = [
  { title: 'Year-round or seasonal?', text: 'Year-round campuses start every Monday. Seasonal locations run on set summer and winter dates.' },
  { title: 'Check the programmes', text: 'Not every programme runs at every location. Each location page lists its programmes.' },
  { title: 'Check the visa rules', text: 'Visa rules depend on your nationality and the country. Our team explains them for you.' },
  { title: 'Compare the costs', text: 'Fees and living costs are different in each location. See our price lists.' },
];

export const seasonalCare: Benefit[] = [
  { title: 'Group leaders all day', text: 'Trained leaders stay with young learners in lessons, activities and trips.' },
  { title: 'Day and night welfare', text: 'A welfare team is on call at all times during the programme.' },
  { title: 'Safe housing', text: 'Students stay in checked residences or host families close to the centre.' },
  { title: 'Updates for families', text: 'Parents receive regular updates and can contact the team at any time.' },
];

export const activityWeek: { day: string; activity: string; cost: string }[] = [
  { day: 'Monday', activity: 'Welcome walk around the campus area', cost: 'Free' },
  { day: 'Tuesday', activity: 'Conversation club', cost: 'Free' },
  { day: 'Wednesday', activity: 'Museum or cultural visit', cost: '000' },
  { day: 'Thursday', activity: 'Sports afternoon', cost: 'Free' },
  { day: 'Friday', activity: 'Quiz night or film evening', cost: 'Free' },
  { day: 'Saturday', activity: 'Full-day trip', cost: '000' },
];

export const contactHours: { day: string; hours: string }[] = [
  { day: 'Monday to Friday', hours: '00:00 – 00:00' },
  { day: 'Saturday', hours: '00:00 – 00:00' },
  { day: 'Sunday', hours: 'Closed (emergency line open)' },
];

export const contactFaqs: Faq[] = [
  { q: 'How quickly will you reply?', a: 'Our admissions team replies to every enquiry by email. If you add a WhatsApp number, we can also send you a message.' },
  { q: 'Can I visit a campus before I book?', a: 'Yes. Ask for a visit in your message and tell us which location you want to see.' },
  { q: 'Which number do I call in an emergency?', a: 'Call or send a WhatsApp message to +971 52 701 4480 at any time.' },
];
